import React, { useState } from 'react';
import { Button, Select, Tooltip } from 'antd';

import { CHEVRON_DOWN, CHEVRON_UP, CLOSE, PLUS } from '../icons';
import { isSystemPrimaryDate, PRIMARY_DATE_MAX_ORDERED } from '../../utils/cards';

/**
 * The card editor's PRIMARY DATE control (docs/plans/primary-date.md; the
 * ordered form in docs/plans/element-sets-and-me.md, "Review answers"): the
 * select in the Primary date row's slot, grown into a small ordered list.
 *
 * `value` is the editor's resolved token — a string ('created', 'updated'
 * or a date element's name: the plain contract, one date) or an array of
 * them (the ordered contract: the first entry with a value leads each item,
 * then the terminal). The first select is the one the row always had;
 * "Add a fallback" appends another (at most PRIMARY_DATE_MAX_ORDERED element
 * NAMES — a terminal token may still end a full list, as the API counts
 * names only — no name twice), each fallback removable and movable with
 * up/down. Created and Updated END the list wherever they are chosen (the
 * API admits a system token only last), so a list never carries one in the
 * middle; a list whose last entry is a date element prints a muted "then
 * Created" — the terminal the API implies.
 *
 * What `onChange` receives is what the card stores: a single entry with no
 * fallbacks stays the plain string; the moment a fallback exists it is the
 * array — and an array that arrived from the API stays an array until its
 * last fallback is removed, so `['due']` (blanks fall to created) round-trips
 * as itself rather than silently becoming `'due'` (blanks show blank).
 */
export default function PrimaryDateList({
	value = 'created',
	// [{ value, label }] — Created · Updated · every date element the card
	// resolves, inherited and set ones labelled with where they come from.
	options = [],
	onChange,
	disabled = false,
	// The first select's accessible name (the row's label).
	label = 'Primary date',
}) {
	const [adding, setAdding] = useState(false);
	const isList = Array.isArray(value);
	const entries = isList ? value : [value];
	const endsWithToken = isSystemPrimaryDate(entries[entries.length - 1]);
	// The cap counts element names, as the API's does: a full list may still
	// take a terminal token, and nothing else.
	const namesBesides = index => entries.filter((token, i) => i !== index && !isSystemPrimaryDate(token)).length;
	const canAdd = !endsWithToken;
	const labelOf = token => options.find(opt => opt.value === token)?.label || token;

	const choose = (index, token) => {
		if (!isList) {
			onChange(token);
			return;
		}
		const next = [...entries];
		next[index] = token;
		// A system token ends the list: whatever followed it goes — and a
		// list that is nothing but Created or Updated is that plain token.
		const cut = isSystemPrimaryDate(token) ? next.slice(0, index + 1) : next;
		if (cut.length === 1 && isSystemPrimaryDate(cut[0])) onChange(cut[0]);
		else onChange(cut);
	};

	const append = (token) => {
		setAdding(false);
		if (!token) return;
		onChange([...entries, token]);
	};

	// Removing the last fallback returns the row to the plain string.
	const remove = (index) => {
		const next = entries.filter((_, i) => i !== index);
		if (next.length <= 1) onChange(next[0] || 'created');
		else onChange(next);
	};

	// A token stays last: neither it nor the entry before it may trade places.
	const canMove = (index, delta) => {
		const target = index + delta;
		if (target < 0 || target >= entries.length) return false;
		return !isSystemPrimaryDate(entries[index]) && !isSystemPrimaryDate(entries[target]);
	};
	const move = (index, delta) => {
		if (!canMove(index, delta)) return;
		const next = [...entries];
		[next[index], next[index + delta]] = [next[index + delta], next[index]];
		onChange(next);
	};

	// Each select offers what the others have not taken; the system tokens
	// are always on offer (choosing one ends the list). Once the OTHER
	// entries hold the most names allowed, only a token is on offer here —
	// so neither an appended select nor a re-chosen terminal makes a ninth.
	const optionsFor = index => options.filter(opt => (
		isSystemPrimaryDate(opt.value)
		|| opt.value === entries[index]
		|| (!entries.includes(opt.value) && namesBesides(index) < PRIMARY_DATE_MAX_ORDERED)
	));

	return (
		<div className={`primary-date-list${isList ? ' primary-date-list--ordered' : ''}`} data-testid='primary-date-list'>
			{entries.map((token, index) => (
				<div key={`${index}-${token}`} className='primary-date-list__row'>
					{index > 0 ? <span className='primary-date-list__then'>then</span> : null}
					<Select
						value={token}
						options={optionsFor(index)}
						onChange={next => choose(index, next)}
						disabled={disabled}
						aria-label={index === 0 ? label : `Fallback ${index}`}
						popupMatchSelectWidth={false}
						className='card-def-primary-date__select'
					/>
					{isList ? (
						<span className='primary-date-list__actions'>
							<Tooltip title='Move up'>
								<Button type='text' size='small' icon={<CHEVRON_UP />} aria-label={`Move ${labelOf(token)} up`} disabled={disabled || !canMove(index, -1)} onClick={() => move(index, -1)} />
							</Tooltip>
							<Tooltip title='Move down'>
								<Button type='text' size='small' icon={<CHEVRON_DOWN />} aria-label={`Move ${labelOf(token)} down`} disabled={disabled || !canMove(index, 1)} onClick={() => move(index, 1)} />
							</Tooltip>
							<Tooltip title='Remove'>
								<Button type='text' size='small' icon={<CLOSE />} aria-label={`Remove ${labelOf(token)}`} disabled={disabled} onClick={() => remove(index)} />
							</Tooltip>
						</span>
					) : null}
				</div>
			))}
			{adding ? (
				<div className='primary-date-list__row'>
					<span className='primary-date-list__then'>then</span>
					<Select
						autoFocus
						open
						placeholder='Choose a date'
						options={optionsFor(entries.length)}
						value={undefined}
						onChange={append}
						onBlur={() => setAdding(false)}
						aria-label='Fallback date'
						popupMatchSelectWidth={false}
						className='card-def-primary-date__select'
					/>
				</div>
			) : null}
			{isList && !endsWithToken && !adding ? (
				<span className='primary-date-list__terminal'>then Created</span>
			) : null}
			{canAdd && !adding ? (
				<Button type='link' size='small' icon={<PLUS />} className='primary-date-list__add' disabled={disabled} onClick={() => setAdding(true)}>
					Add a fallback
				</Button>
			) : null}
		</div>
	);
}
